import { ArrowLeft, ArrowRight, Copy, Repeat2, Scissors, Trash2, VolumeX } from 'lucide-react';
import { sliceCount } from '../audio/slice';
import { useStore } from '../store/useStore';
import type { FilterKind, Pattern, SourceMeta, Step } from '../types';
import { Select } from './ui/Controls';
import { Knob } from './ui/Knob';

interface Props {
  pattern: Pattern;
  source: SourceMeta | null;
  color: string;
}

/** Per-step editor. Edits apply to every selected step at once. */
export function StepInspector({ pattern, source, color }: Props) {
  const selected = useStore((s) => s.selectedSteps);
  const project = useStore((s) => s.project);
  const updateSteps = useStore((s) => s.updateSteps);
  const duplicateStep = useStore((s) => s.duplicateStep);
  const deleteStep = useStore((s) => s.deleteStep);
  const insertRest = useStore((s) => s.insertRest);
  const moveStep = useStore((s) => s.moveStep);

  const steps = pattern.steps.filter((s) => selected.includes(s.id));
  if (!steps.length) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-[11px] leading-relaxed text-ink-500">
        Select a step to edit it.
        <br />
        Drag to reorder · alt-drag to copy · right-click to reverse · double-click to mute.
      </div>
    );
  }

  const first = steps[0];
  const patch = (p: Partial<Step>) => updateSteps(pattern.id, selected, p);
  const maxSlice = source ? sliceCount(source, project.beatsPerBar) - 1 : 15;
  const common = <K extends keyof Step>(key: K): Step[K] => first[key];

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">
      <div className="flex items-center justify-between">
        <span className="label">
          {steps.length === 1 ? `Step ${pattern.steps.indexOf(first) + 1}` : `${steps.length} steps`}
        </span>
        <div className="flex gap-1">
          <button
            className="btn btn-icon"
            title="Duplicate step"
            onClick={() => duplicateStep(pattern.id, pattern.steps.indexOf(first))}
          >
            <Copy size={12} />
          </button>
          <button
            className="btn btn-icon"
            title="Insert rest before"
            onClick={() => insertRest(pattern.id, pattern.steps.indexOf(first))}
          >
            <Scissors size={12} />
          </button>
          <button
            className="btn btn-icon"
            title="Delete step"
            onClick={() => deleteStep(pattern.id, pattern.steps.indexOf(first))}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <span className="label w-10">Slice</span>
          <input
            type="range"
            className="flex-1"
            min={-1}
            max={maxSlice}
            step={1}
            value={common('slice')}
            onChange={(e) => patch({ slice: parseInt(e.target.value, 10) })}
          />
          {/* Typing a slice number beats dragging a slider to position 37. */}
          <input
            type="number"
            className="field w-14 text-center"
            min={0}
            max={maxSlice + 1}
            value={common('slice') < 0 ? '' : common('slice') + 1}
            placeholder="—"
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              patch({ slice: Number.isFinite(v) ? Math.max(-1, Math.min(maxSlice, v - 1)) : -1 });
            }}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>

        <div className="flex items-center gap-1">
          <span className="label w-10">Move</span>
          <button
            className="btn btn-icon flex-1 justify-center"
            title="Move this step earlier"
            onClick={() => moveStep(pattern.id, pattern.steps.indexOf(first), pattern.steps.indexOf(first) - 1)}
          >
            <ArrowLeft size={12} />
          </button>
          <button
            className="btn btn-icon flex-1 justify-center"
            title="Move this step later"
            onClick={() => moveStep(pattern.id, pattern.steps.indexOf(first), pattern.steps.indexOf(first) + 1)}
          >
            <ArrowRight size={12} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        <button
          className={`btn ${common('reverse') ? 'btn-active' : ''}`}
          onClick={() => patch({ reverse: !common('reverse') })}
        >
          <Repeat2 size={12} />
          Reverse
        </button>
        <button
          className={`btn ${common('mute') ? 'btn-active' : ''}`}
          onClick={() => patch({ mute: !common('mute') })}
        >
          <VolumeX size={12} />
          Mute
        </button>
      </div>

      <div className="grid grid-cols-3 gap-y-3">
        <Knob
          label="Gain"
          value={common('gain')}
          min={0}
          max={2}
          color={color}
          format={(v) => `${(20 * Math.log10(Math.max(0.001, v))).toFixed(1)}dB`}
          onChange={(v) => patch({ gain: v })}
        />
        <Knob
          label="Pan"
          value={common('pan')}
          min={-1}
          max={1}
          bipolar
          color={color}
          format={(v) => (Math.abs(v) < 0.02 ? 'C' : `${v < 0 ? 'L' : 'R'}${Math.abs(v * 100).toFixed(0)}`)}
          onChange={(v) => patch({ pan: v })}
        />
        <Knob
          label="Pitch"
          value={common('pitch')}
          min={-24}
          max={24}
          step={1}
          bipolar
          color={color}
          format={(v) => `${v > 0 ? '+' : ''}${v.toFixed(0)}st`}
          onChange={(v) => patch({ pitch: v })}
        />
        <Knob
          label="Gate"
          value={common('gate')}
          min={0.05}
          max={1}
          color={color}
          format={(v) => `${Math.round(v * 100)}%`}
          onChange={(v) => patch({ gate: v })}
        />
        <Knob
          label="Repeat"
          value={common('repeat')}
          min={1}
          max={16}
          step={1}
          color={color}
          format={(v) => `×${v.toFixed(0)}`}
          onChange={(v) => patch({ repeat: Math.round(v) })}
        />
        <Knob
          label="Ramp"
          value={common('repeatRamp')}
          min={-1}
          max={1}
          bipolar
          color={color}
          format={(v) => (v < 0 ? 'decay' : v > 0 ? 'swell' : 'flat')}
          onChange={(v) => patch({ repeatRamp: v })}
        />
        <Knob
          label="Chance"
          value={common('probability')}
          min={0}
          max={1}
          color={color}
          format={(v) => `${Math.round(v * 100)}%`}
          onChange={(v) => patch({ probability: v })}
        />
        <Knob
          label="Nudge"
          value={common('nudge')}
          min={-0.5}
          max={0.5}
          bipolar
          color={color}
          format={(v) => `${(v * 100).toFixed(0)}%`}
          onChange={(v) => patch({ nudge: v })}
        />
        <Knob
          label="Rate"
          value={common('rate')}
          min={0.25}
          max={4}
          curve={2}
          color={color}
          format={(v) => `${v.toFixed(2)}×`}
          onChange={(v) => patch({ rate: v })}
        />
      </div>

      <div className="space-y-2 border-t border-ink-700 pt-2">
        <Select<FilterKind>
          value={common('filter')}
          options={[
            { value: 'off', label: 'Filter off' },
            { value: 'lowpass', label: 'Low pass' },
            { value: 'highpass', label: 'High pass' },
            { value: 'bandpass', label: 'Band pass' },
          ]}
          onChange={(v) => patch({ filter: v })}
          className="w-full"
        />
        {common('filter') !== 'off' && (
          <div className="grid grid-cols-2">
            <Knob
              label="Cutoff"
              value={common('cutoff')}
              min={40}
              max={18000}
              curve={3}
              color={color}
              format={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v.toFixed(0)}`)}
              onChange={(v) => patch({ cutoff: v })}
            />
            <Knob
              label="Res"
              value={common('resonance')}
              min={0.1}
              max={20}
              curve={2}
              color={color}
              onChange={(v) => patch({ resonance: v })}
            />
          </div>
        )}
      </div>
    </div>
  );
}
